import { useState, useCallback, useRef } from 'react';

export interface UndoableAction {
  type: string;
  description: string;
  undo: () => Promise<void> | void;
  redo: () => Promise<void> | void;
  timestamp: number;
}

const MAX_HISTORY_SIZE = 50;

export function useUndoRedo() {
  const [undoStack, setUndoStack] = useState<UndoableAction[]>([]);
  const [redoStack, setRedoStack] = useState<UndoableAction[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const lastActionRef = useRef<number>(0);

  const pushAction = useCallback((action: Omit<UndoableAction, 'timestamp'>) => {
    const timestamp = Date.now();
    // Debounce rapid consecutive actions (within 100ms)
    if (timestamp - lastActionRef.current < 100) {
      return;
    }
    lastActionRef.current = timestamp;

    setUndoStack((prev) => {
      const newStack = [...prev, { ...action, timestamp }];
      // Keep only the last MAX_HISTORY_SIZE actions
      return newStack.slice(-MAX_HISTORY_SIZE);
    });
    // Clear redo stack when a new action is performed
    setRedoStack([]);
  }, []);

  const undo = useCallback(async () => {
    if (undoStack.length === 0 || isProcessing) return;

    setIsProcessing(true);
    try {
      const action = undoStack[undoStack.length - 1];
      await action.undo();

      setUndoStack((prev) => prev.slice(0, -1));
      setRedoStack((prev) => [...prev, action]);
    } finally {
      setIsProcessing(false);
    }
  }, [undoStack, isProcessing]);

  const redo = useCallback(async () => {
    if (redoStack.length === 0 || isProcessing) return;

    setIsProcessing(true);
    try {
      const action = redoStack[redoStack.length - 1];
      await action.redo();

      setRedoStack((prev) => prev.slice(0, -1));
      setUndoStack((prev) => [...prev, action]);
    } finally {
      setIsProcessing(false);
    }
  }, [redoStack, isProcessing]);

  const canUndo = undoStack.length > 0 && !isProcessing;
  const canRedo = redoStack.length > 0 && !isProcessing;
  const lastAction = undoStack.length > 0 ? undoStack[undoStack.length - 1] : null;
  const nextRedoAction = redoStack.length > 0 ? redoStack[redoStack.length - 1] : null;

  const clearHistory = useCallback(() => {
    setUndoStack([]);
    setRedoStack([]);
  }, []);

  return {
    pushAction,
    undo,
    redo,
    canUndo,
    canRedo,
    isProcessing,
    lastAction,
    nextRedoAction,
    undoStackSize: undoStack.length,
    redoStackSize: redoStack.length,
    clearHistory,
  };
}
